import { useEffect, useState } from 'react';

/**
 * ¿Estamos en una pantalla de teléfono?
 *
 * POR QUÉ UN HOOK Y NO UNA MEDIA QUERY
 * Todo el módulo de indicadores está escrito con estilos EN LÍNEA — cero
 * `className` en 40 componentes— y un estilo en línea no admite `@media`.
 * Reescribirlo entero a CSS para poder adaptarlo a móvil sería un cambio de
 * radio enorme sobre una vista que hoy funciona en escritorio.
 *
 * Así que el punto de corte se decide en JavaScript y cada componente elige su
 * disposición. Es la misma decisión que ya tomó la app del vendedor con
 * `useIsDesktop(1024)`.
 *
 * ⚠️ SE ESCUCHAN LOS CAMBIOS, no se lee una sola vez. Girar el teléfono cambia
 * el ancho sin recargar la página, y una lectura única dejaría la vista en la
 * disposición equivocada hasta que el usuario navegara a otro sitio.
 *
 * El valor inicial se calcula ya en el primer render (no en un efecto) para que
 * el teléfono no pinte primero la versión de escritorio y salte.
 */
/**
 * Punto de corte, en píxeles.
 *
 * ⚠️ NO es un número redondo elegido a ojo: es el ancho MÍNIMO en el que la
 * disposición de escritorio cabe sin que la página se arrastre de lado. Medido
 * en producción, con la vista Global real:
 *
 *     761 px  ->  171 px de desborde
 *     800 px  ->  132
 *     860 px  ->   72
 *     900 px  ->   32
 *     960 px  ->    0   <- aquí deja de desbordar
 *
 * ⚠️ Pero NO DESBORDAR no es lo mismo que VERSE BIEN. Entre 960 y 1152 el panel
 * de filtros no se salía de la pantalla: se ENVOLVÍA, y PERIODO caía a una
 * segunda fila pegado a la izquierda. Envolverse no produce scroll horizontal,
 * así que la comprobación de desbordamiento no lo veía — lo reportó un usuario
 * desde su portátil.
 *
 * 1152 es el ancho a partir del cual las dos zonas del panel caben en una fila
 * con la base de flex corregida (ver `Filtros.tsx`). Por debajo, la disposición
 * apilada es mejor que una envuelta a medias.
 *
 * Si algún día la fila de filtros crece, este número sube con ella.
 */
export const CORTE_MOVIL = 1152;

export function useEsMovil(limite: number = CORTE_MOVIL): boolean {
  const consulta = `(max-width: ${limite}px)`;

  const [esMovil, setEsMovil] = useState<boolean>(() => {
    // `window` puede no existir (pruebas con jsdom parcial, render en servidor).
    // Ante la duda, ESCRITORIO: es la disposición que existía antes de este
    // cambio, así que un fallo del hook degrada a lo conocido y no a lo nuevo.
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(consulta).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(consulta);
    const alCambiar = (e: MediaQueryListEvent | MediaQueryList) =>
      setEsMovil(e.matches);

    alCambiar(mql); // por si cambió entre el primer render y el efecto
    // Safari antiguo no tiene addEventListener sobre MediaQueryList.
    if (mql.addEventListener) {
      mql.addEventListener('change', alCambiar);
      return () => mql.removeEventListener('change', alCambiar);
    }
    mql.addListener(alCambiar);
    return () => mql.removeListener(alCambiar);
  }, [consulta]);

  return esMovil;
}

export default useEsMovil;
